import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { useBranches } from '../../hooks/useBranches';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { usePermission } from '../../hooks/usePermission';
import { date, money, today } from '../../utils/format';
import { Empty, ErrorNotice, Loading, PageHeader, Table } from '../../components/ui';
import { DateInput, Drawer, Field, NumberInput, Select, TextInput } from '../../components/ui/Form';
import { VoidDialog } from '../../components/ui/VoidDialog';
import { useToast } from '../../components/ui/Toast';

interface IncomeRow {
  id: string;
  doc_no: string;
  doc_date: string;
  payer: string;
  narration: string;
  category: string;
  receipt_mode: string;
  total_amount: string;
  status: string;
}

interface Category {
  id: string;
  code: string;
  name: string;
}

/** Money in that is not a sale: commission, scrap, rent recovered, a refund. */
export function IncomePage() {
  const { can } = usePermission();
  const client = useQueryClient();
  const onEnter = useKeyboardForm();
  const { branches, defaultBranchId } = useBranches();

  const [open, setOpen] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [voiding, setVoiding] = useState<IncomeRow | null>(null);
  const toast = useToast();
  const [form, setForm] = useState({
    docDate: today(),
    accountId: '',
    customerId: '',
    payerName: '',
    narration: '',
    referenceNo: '',
    amount: '',
    taxAmount: '',
    receiptMode: 'CASH',
    branchId: '',
  });

  const list = useQuery({ queryKey: ['income'], queryFn: () => api.get<{ data: IncomeRow[] }>('/income') });
  const categories = useQuery({
    queryKey: ['incomeCategories'],
    queryFn: () => api.get<{ data: Category[] }>('/income-categories'),
    staleTime: 600_000,
  });
  const customers = useQuery({ queryKey: ['customers', ''], queryFn: () => endpoints.parties.customers() });
  const customerList = (customers.data?.data ?? []) as unknown as { id: string; name: string }[];

  const create = useMutation({
    mutationFn: () =>
      api.post(
        '/income',
        {
          branchId: form.branchId || defaultBranchId,
          docDate: form.docDate,
          accountId: form.accountId,
          ...(form.customerId ? { customerId: form.customerId } : {}),
          ...(form.payerName ? { payerName: form.payerName } : {}),
          narration: form.narration,
          ...(form.referenceNo ? { referenceNo: form.referenceNo } : {}),
          amount: form.amount,
          ...(form.taxAmount ? { taxAmount: form.taxAmount } : {}),
          receiptMode: form.receiptMode,
        },
        { 'idempotency-key': crypto.randomUUID() },
      ),
    onSuccess: () => {
      setOpen(false);
      setForm({ ...form, narration: '', payerName: '', amount: '', taxAmount: '', referenceNo: '' });
      setError(null);
      void client.invalidateQueries({ queryKey: ['income'] });
    },
    onError: setError,
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    create.mutate();
  };

  const rows = list.data?.data ?? [];
  const ready = form.accountId !== '' && form.narration.trim().length >= 3 && form.amount !== '';

  const voidIncome = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.post(`/income/${id}/cancel`, { reason }),
    onSuccess: () => {
      toast(`${voiding?.doc_no ?? 'Receipt'} voided`, 'The money is reversed out of cash or bank.');
      void client.invalidateQueries({ queryKey: ['income'] });
      setVoiding(null);
    },
    onError: (e) => {
      setError(e);
      setVoiding(null);
    },
  });

  return (
    <>
      <PageHeader
        title="Other income"
        subtitle="Money in that is not a sale — commission, scrap, rent recovered."
        actions={
          can('income.create') ? (
            <button className="btn-primary" onClick={() => setOpen(true)}>
              Record income
            </button>
          ) : null
        }
      />
      <ErrorNotice error={error} />

      {list.isLoading ? <Loading /> : null}
      {!list.isLoading && rows.length === 0 ? (
        <Empty
          message="No other income recorded yet."
          hint="Commission, scrap sales, rent recovered — anything that comes in without a piece of jewellery going out."
          action={can('income.create') ? { label: 'Record income', onClick: () => setOpen(true) } : undefined}
        />
      ) : null}

      {rows.length > 0 ? (
        <Table head={['Date', 'Document', 'From', 'For', 'Category', 'Mode', 'Amount', 'Status', '']}>
          {rows.map((i) => (
            <tr key={i.id} className={i.status === 'CANCELLED' ? 'opacity-60' : ''}>
              <td className="td">{date(i.doc_date)}</td>
              <td className="td num">{i.doc_no}</td>
              <td className="td">{i.payer}</td>
              <td className="td text-xs">{i.narration}</td>
              <td className="td text-xs text-ink-soft">{i.category}</td>
              <td className="td text-xs">{i.receipt_mode.toLowerCase()}</td>
              <td className="td num text-right font-medium">{money(i.total_amount)}</td>
              <td className="td text-xs">{(i.status ?? 'POSTED').toLowerCase()}</td>
              <td className="td text-right">
                {i.status !== 'CANCELLED' && can('document.void') ? (
                  <button className="text-xs text-danger underline" onClick={() => setVoiding(i)}>
                    Void
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </Table>
      ) : null}

      {open ? (
        <Drawer
          title="Record income"
          subtitle="Anything received that is not the sale of jewellery."
          onClose={() => setOpen(false)}
        >
          <ErrorNotice error={error} />
          <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="space-y-4">
            <Field label="Category" required>
              <Select
                value={form.accountId}
                onChange={(v) => setForm({ ...form, accountId: v })}
                options={(categories.data?.data ?? []).map((c) => ({ value: c.id, label: `${c.code} ${c.name}` }))}
              />
            </Field>

            <Field label="Date" required>
              <DateInput value={form.docDate} onChange={(v) => setForm({ ...form, docDate: v })} />
            </Field>

            <Field label="Customer" hint="Only when it is somebody on the books.">
              <Select
                value={form.customerId}
                onChange={(v) => setForm({ ...form, customerId: v })}
                options={customerList.map((c) => ({ value: c.id, label: c.name }))}
              />
            </Field>

            <Field label="Or received from">
              <TextInput value={form.payerName} onChange={(v) => setForm({ ...form, payerName: v })} />
            </Field>

            <Field label="What it was for" required>
              <TextInput
                value={form.narration}
                onChange={(v) => setForm({ ...form, narration: v })}
                placeholder="Sweepings sold to the refiner"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Amount" required>
                <NumberInput value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} suffix="₹" />
              </Field>
              <Field label="GST charged" hint="Only if you charged tax on it.">
                <NumberInput value={form.taxAmount} onChange={(v) => setForm({ ...form, taxAmount: v })} suffix="₹" />
              </Field>
            </div>

            <Field label="Received by" required>
              <Select
                value={form.receiptMode}
                onChange={(v) => setForm({ ...form, receiptMode: v })}
                options={[
                  { value: 'CASH', label: 'Cash' },
                  { value: 'BANK', label: 'Bank transfer' },
                  { value: 'UPI', label: 'UPI' },
                  { value: 'CARD', label: 'Card' },
                  { value: 'CHEQUE', label: 'Cheque' },
                  { value: 'CREDIT', label: 'Not yet received (owed)' },
                ]}
              />
            </Field>

            {form.receiptMode === 'CREDIT' && !form.customerId ? (
              <p className="rounded border border-gold-400 bg-gold-50 p-2 text-xs">
                Income not yet received has to be owed by somebody on the books. Name the customer, or record how it was
                received.
              </p>
            ) : null}

            {branches.length > 1 ? (
              <Field label="Branch" required>
                <Select
                  value={form.branchId || (defaultBranchId ?? '')}
                  onChange={(v) => setForm({ ...form, branchId: v })}
                  options={branches.map((b) => ({ value: b.id, label: b.name }))}
                />
              </Field>
            ) : null}

            <div className="flex gap-2 pt-2">
              <button className="btn-primary" disabled={!ready || create.isPending}>
                {create.isPending ? 'Recording…' : 'Record income'}
              </button>
              <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>
                Cancel
              </button>
            </div>
          </form>
        </Drawer>
      ) : null}
      <VoidDialog
        docNo={voiding?.doc_no ?? null}
        what="receipt"
        effect="The money comes back out of the cash or bank it went into, and off the income report, in a reversing entry dated today."
        busy={voidIncome.isPending}
        onCancel={() => setVoiding(null)}
        onConfirm={(reason) => voiding && voidIncome.mutate({ id: voiding.id, reason })}
      />
    </>
  );
}
